import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import routes from '../../config/i18next/locales/nl/routes.json';

import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';

import LogoJellinekZelfhulp from '../../assets/images/logos/jellinekzelfhulp.svg';
import LogoZelfhulpAlcohol from '../../assets/images/logos/zelfhulpalcohol.svg';
import LogoZelfhulpCannabis from '../../assets/images/logos/zelfhulpcannabis.svg';
import LogoZelfhulpCocaine from '../../assets/images/logos/zelfhulpcocaine.svg';
import LogoZelfhulpGokken from '../../assets/images/logos/zelfhulpgokken.svg';
import LogoZelfhulpTabak from '../../assets/images/logos/zelfhulptabak.svg';
import LogoZelfhulpNaasten from '../../assets/images/logos/zelfhulpnaasten.svg';
import LogoZelfhulpBegeleid from '../../assets/images/logos/zelfhulpbegeleid.svg';
import LogoLoketKansspel from '../../assets/images/logos/vendor/logo-loketkansspel.svg';

import {
  DOMAIN_JELLINEK_ZELFHULP,
  DOMAIN_ZELFHULP_ALCOHOL,
  DOMAIN_ZELFHULP_CANNABIS,
  DOMAIN_ZELFHULP_COCAINE,
  DOMAIN_ZELFHULP_GOKKEN,
  DOMAIN_ZELFHULP_NAASTEN,
  DOMAIN_ZELFHULP_TABAK,
  DOMAIN_ZELFHULP_BEGELEID,
  IS_REACT_NATIVE,
  ENABLE_THEMING,
} from '../../config/constants';

import './style.scss';

interface Props {
  siteName: string;
  className?: string;
  content?: () => React.ReactNode;
}

export const Header: React.FC<Props> = ({
  siteName,
  className,
  content,
}) => {
  const { landing, program } = useRoutes();
  const { buildClass, joinClasses } = useClasses('SiteHeader');

  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));

  const headerRef = React.useRef<HTMLElement>();

  React.useEffect(() => {
    const header = headerRef.current;

    if (!header) {
      return;
    }

    function setOffset() {
      const headerHeight = header.getBoundingClientRect().height;

      document.documentElement.style.scrollPaddingTop = `${headerHeight}px`;
    }

    setOffset();

    window.addEventListener('resize', setOffset);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', setOffset);
      document.documentElement.style.scrollPaddingTop = '0px';
    };
  }, []);

  const siteLogo = useMemo(() => {
    switch (siteName) {
      case DOMAIN_JELLINEK_ZELFHULP:
        return LogoJellinekZelfhulp;

      case DOMAIN_ZELFHULP_ALCOHOL:
        return LogoZelfhulpAlcohol;

      case DOMAIN_ZELFHULP_CANNABIS:
        return LogoZelfhulpCannabis;

      case DOMAIN_ZELFHULP_COCAINE:
        return LogoZelfhulpCocaine;

      case DOMAIN_ZELFHULP_GOKKEN:
        return ENABLE_THEMING ? LogoLoketKansspel : LogoZelfhulpGokken;

      case DOMAIN_ZELFHULP_TABAK:
        return LogoZelfhulpTabak;

      case DOMAIN_ZELFHULP_NAASTEN:
        return LogoZelfhulpNaasten;

      case DOMAIN_ZELFHULP_BEGELEID:
        return LogoZelfhulpBegeleid;

      default:
        return '';
    }
  }, [siteName]);

  const homeLink = useMemo(() => {
    if (siteName !== DOMAIN_ZELFHULP_BEGELEID && isLoggedIn) {
      return program;
    }

    // On react native, don't allow navigation to the root, always go to login
    // The homepage isnt used in the native app
    if (IS_REACT_NATIVE) {
      return routes.zelfhulp.login;
    }

    return landing;
  }, [siteName, isLoggedIn, program, landing]);

  return (
    <header
      className={joinClasses(buildClass(), className)}
      role="banner"
      ref={headerRef}
    >
      <Container>
        <div className={joinClasses(buildClass('inner'), 'row')}>
          <div className="col-4">
            <Link to={homeLink} className={buildClass('logo', siteName)}>
              <img src={siteLogo} alt={siteName} />
            </Link>
          </div>

          {content && (
            <div className="col-8">
              <div className={buildClass('content')}>
                {content()}
              </div>
            </div>
          )}
        </div>
      </Container>
    </header>
  );
};

export default Header;
